import { RootState, store } from 'store';

export const useWaitForReduxChange = () => {
    const waitForStateChange = <T>(
        selector: (state: RootState) => T,
        targetValue: T,
        timeout = 5000
    ): Promise<void> => {
        return new Promise((resolve, reject) => {
            let unsubscribed = false;

            // Set up timeout
            const timeoutId = setTimeout(() => {
                if (!unsubscribed) {
                    unsubscribed = true;
                    reject(new Error(`Timeout waiting for state change after ${timeout}ms`));
                }
            }, timeout);

            const checkState = () => {
                if (unsubscribed) return;

                try {
                    const currentValue = selector(store.getState());
                    if (currentValue === targetValue) {
                        unsubscribed = true;
                        clearTimeout(timeoutId);
                        resolve();
                    }
                } catch (error) {
                    unsubscribed = true;
                    clearTimeout(timeoutId);
                    reject(error);
                }
            };

            // Check immediately in case the state is already at target value
            checkState();

            if (!unsubscribed) {
                // Subscribe to store changes
                const unsubscribe = store.subscribe(() => {
                    if (!unsubscribed) {
                        checkState();
                    }
                });

                // Cleanup when promise resolves/rejects
                const cleanup = () => {
                    if (!unsubscribed) {
                        unsubscribed = true;
                        unsubscribe();
                        clearTimeout(timeoutId);
                    }
                };

                // Add cleanup to promise chain
                Promise.resolve().then(() => {
                    if (unsubscribed) {
                        unsubscribe();
                    }
                });
            }
        });
    };

    return { waitForStateChange };
};
